'use client'

import {useRef, useState, type MouseEvent} from 'react'
import {Loader2} from 'lucide-react'
import type {ChatMessage} from '@/types'
import {getAuthToken} from '@/lib/api'

type VoiceMessageBubbleProps = {
    message: ChatMessage
    accountWxid: string
    direction: 'in' | 'out'
    transcript?: string
    transcribing?: boolean
    onContextMenu: (event: MouseEvent, message: ChatMessage) => void
}

// Extract seconds from the content (e.g. "[语音] 12\"" → 12). Fallback to 1.
function extractSeconds(content: string): number {
    const match = content.match(/(\d+)/)
    if (!match) return 1
    return Math.max(1, Number(match[1]) || 1)
}

/**
 * "Laid-down wifi" speaker glyph. Three arcs radiate to the right from a
 * shared origin (the tiny dot). All three are visible by default so the
 * idle state looks like a solid wifi-fan. While playing, the arcs light
 * up inner → middle → outer in sequence (wifi-connection animation) then
 * reset, giving the impression of a signal pulsing outward.
 *
 * `.voice-wave-N` + the `.voice-wave-animate` wrapper on the parent
 * toggles the staged animation via CSS delays declared in index.css.
 */
function WavesIcon({playing}: {playing: boolean}) {
    return (
        <svg
            width="22"
            height="22"
            viewBox="0 0 22 22"
            fill="none"
            aria-hidden="true"
            className={playing ? 'voice-wave-animate' : ''}
        >
            {/* origin dot */}
            <circle cx="6" cy="11" r="1.5" fill="currentColor"/>
            {/* inner arc */}
            <path
                d="M9 8 Q11.5 11 9 14"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                fill="none"
                className="voice-wave voice-wave-1"
            />
            {/* middle arc */}
            <path
                d="M12.5 5.5 Q16.5 11 12.5 16.5"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                fill="none"
                className="voice-wave voice-wave-2"
            />
            {/* outer arc */}
            <path
                d="M16 3 Q21.5 11 16 19"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                fill="none"
                className="voice-wave voice-wave-3"
            />
        </svg>
    )
}

export function VoiceMessageBubble({
    message,
    accountWxid,
    direction,
    transcript,
    transcribing = false,
    onContextMenu,
}: VoiceMessageBubbleProps) {
    const audioRef = useRef<HTMLAudioElement | null>(null)
    const [playing, setPlaying] = useState(false)
    const [audioFailed, setAudioFailed] = useState(false)
    const [duration, setDuration] = useState<number | null>(null)
    // Negative ids mark optimistic (pending) rows — they have no server
    // record yet, so hitting /messages/voice?id=<negative> would 400.
    // Use the local blob URL when present; otherwise render as a silent
    // placeholder until SSE reconciles the real id.
    const hasPlayableSource = Boolean(message.localPreviewUrl) || message.id > 0
    const voiceParams = new URLSearchParams({id: String(message.id), wxid: accountWxid})
    const token = getAuthToken()
    if (token) voiceParams.set('access_token', token)
    const voiceUrl = message.localPreviewUrl
        || (message.id > 0 ? `/api/messages/voice?${voiceParams.toString()}` : '')

    const seconds = duration && Number.isFinite(duration) ? Math.max(1, Math.round(duration)) : extractSeconds(message.content)
    const outgoing = direction === 'out'

    async function togglePlayback() {
        if (audioFailed || !hasPlayableSource) return
        const audio = audioRef.current
        if (!audio) return
        if (playing) {
            audio.pause()
            return
        }
        try {
            await audio.play()
        } catch {
            setAudioFailed(true)
        }
    }

    return (
        <div className={`flex flex-col ${outgoing ? 'items-end' : 'items-start'}`}>
            {hasPlayableSource ? (
                <audio
                    ref={audioRef}
                    preload="metadata"
                    src={voiceUrl}
                    onLoadedMetadata={(event) => setDuration(event.currentTarget.duration)}
                    onError={() => setAudioFailed(true)}
                    onPlay={() => setPlaying(true)}
                    onPause={() => setPlaying(false)}
                    onEnded={() => setPlaying(false)}
                />
            ) : null}
            <div
                className={`rounded-lg shadow-sm px-3 min-h-10 flex items-center ${
                    outgoing
                        ? 'bg-bubble-out text-ink self-end'
                        : 'bg-white border border-bubble-inBorder text-ink self-start'
                }`}
            >
                <div
                    className={`flex items-center gap-2 w-24 h-[22px] min-h-[22px] shrink-0 cursor-pointer ${
                        outgoing ? 'justify-end' : 'justify-start'
                    } ${audioFailed ? 'opacity-60' : ''}`}
                onClick={togglePlayback}
                onContextMenu={(event) => onContextMenu(event, message)}
                title={audioFailed ? '语音不可用' : playing ? '点击暂停' : '点击播放 / 右键可操作'}
                >
                    {outgoing ? (
                        <>
                            <span className="text-sm leading-none shrink-0 ml-auto">{seconds}&quot;</span>
                            {/* mirror horizontally for outgoing so the waves
                                always radiate toward the outside of the bubble */}
                            <span className="scale-x-[-1] inline-flex w-[22px] h-[22px] shrink-0">
                                <WavesIcon playing={playing}/>
                            </span>
                        </>
                    ) : (
                        <>
                            <span className="text-sm leading-none shrink-0 ml-auto">{seconds}&quot;</span>
                            <span className="inline-flex w-[22px] h-[22px] shrink-0">
                                <WavesIcon playing={playing}/>
                            </span>
                        </>
                    )}
                </div>
            </div>
            {transcribing ? (
                <div
                    className="mt-2 w-full min-w-24 flex items-center justify-center"
                    role="status"
                    aria-label="正在转写语音"
                >
                    <Loader2 size={16} className="animate-spin text-gray-500"/>
                </div>
            ) : transcript ? (
                <div
                    className={`mt-2 w-full min-w-24 max-w-[320px] text-sm text-gray-700 whitespace-pre-wrap break-words ${
                        outgoing ? 'text-right' : 'text-left'
                    }`}
                >
                    {transcript}
                </div>
            ) : null}
        </div>
    )
}
